import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Download, ImageUp, Plus, RotateCcw } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import { Button, Pill, Seg, type PillTone } from '../../components/ui';
import {
  VIDEO_GEN_MAX_MB,
  VIDEO_GEN_MAX_SIDE_RATIO,
  VIDEO_GEN_MIN_SIDE_PX,
  useCurrentUser,
  useRetryVideoGen,
  useStartVideoGen,
  useVideoGenStatus,
  useVideoGenerations,
} from '../../hooks';
import { authorLabel } from '../../lib/author';
import { downloadFile } from '../../lib/download';
import type {
  Id,
  VideoGenStage,
  VideoGenStatus,
  VideoGenTab,
  VideoGeneration,
} from '../../types/api';
import styles from './VideoGen.module.css';

const TABS: { value: VideoGenTab; label: string }[] = [
  { value: 'anchor', label: 'Диктор' },
  { value: 'live_photo', label: 'Живое фото' },
];

const TAB_HINTS: Record<VideoGenTab, string> = {
  anchor: 'Человек на фото говорит в камеру: камера неподвижна, без жестов руками и головой.',
  live_photo: 'Плавный наезд камеры: картинка выглядит как живая съёмка.',
};

const STEPS: { key: VideoGenStage; label: string }[] = [
  { key: 'submitted', label: 'Отправлено' },
  { key: 'pending', label: 'В очереди' },
  { key: 'in_progress', label: 'Генерация' },
  { key: 'done', label: 'Готово' },
];

const STATUS_LABELS: Record<VideoGenStatus, string> = {
  running: 'в работе',
  done: 'готово',
  failed: 'ошибка',
};

const STATUS_TONES: Record<VideoGenStatus, PillTone> = {
  running: 'info',
  done: 'ok',
  failed: 'danger',
};

const IMAGE_TYPES = ['image/jpeg', 'image/png'];
const NO_CREDIT_MESSAGE = 'Пополните баланс OpenRouter';

interface PickedImage {
  file: File;
  url: string;
  width: number;
  height: number;
}

function ago(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} ч`;
  return `${Math.round(hours / 24)} дн`;
}

function daysLabel(n: number): string {
  const tens = n % 100;
  const ones = n % 10;
  if (tens >= 11 && tens <= 14) return `${n} дней`;
  if (ones === 1) return `${n} день`;
  if (ones >= 2 && ones <= 4) return `${n} дня`;
  return `${n} дней`;
}

// OpenRouter reports costs like 0.1555, which is 0.15549999… as a float and toFixed() rounds down.
const ROUNDING_NUDGE = 1e-9;

function money(usd: number | null): string {
  return usd === null ? '—' : `$${(usd + ROUNDING_NUDGE).toFixed(3)}`;
}

/** Same limits as the server, checked before uploading so a bad file costs nothing. */
async function checkImage(file: File): Promise<PickedImage | string> {
  if (!IMAGE_TYPES.includes(file.type)) return 'Нужна картинка JPG или PNG';
  if (file.size > VIDEO_GEN_MAX_MB * 1024 * 1024) return `Картинка больше ${VIDEO_GEN_MAX_MB} МБ`;
  let width: number;
  let height: number;
  try {
    const bitmap = await createImageBitmap(file);
    ({ width, height } = bitmap);
    bitmap.close();
  } catch {
    return 'Не удалось открыть картинку — файл повреждён?';
  }
  if (Math.min(width, height) < VIDEO_GEN_MIN_SIDE_PX) {
    return `Картинка меньше ${VIDEO_GEN_MIN_SIDE_PX} px по одной из сторон`;
  }
  if (Math.max(width, height) / Math.min(width, height) > VIDEO_GEN_MAX_SIDE_RATIO) {
    return 'Слишком вытянутая картинка: одна сторона больше другой в 2,5 раза';
  }
  return { file, url: URL.createObjectURL(file), width, height };
}

export function VideoGenPage() {
  const { can, isAdmin, user } = useCurrentUser();
  const canEdit = can('editor');

  const inputRef = useRef<HTMLInputElement>(null);
  const [mountedAt] = useState(() => Date.now());
  const [tab, setTab] = useState<VideoGenTab>('anchor');
  const [selected, setSelected] = useState<Id | 'new'>('new');
  const [picked, setPicked] = useState<PickedImage | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  const statusQ = useVideoGenStatus();
  const listQ = useVideoGenerations({ limit: 100 });
  const generations = useMemo(() => listQ.data?.items ?? [], [listQ.data]);
  const generation =
    selected === 'new' ? null : (generations.find((g) => g.id === selected) ?? null);

  const start = useStartVideoGen((p) => setUploadPct(Math.round(p.fraction * 100)));
  const retry = useRetryVideoGen();

  useEffect(
    () => () => {
      if (picked) URL.revokeObjectURL(picked.url);
    },
    [picked],
  );

  const mine = generations.filter((g) => g.author?.login === user?.username);
  const busy = mine.some((g) => g.status === 'running');
  // After a 402 the button stays off until reload, even if the cached balance still says ok.
  const hit402 = mine.some(
    (g) =>
      g.error_code === 'insufficient_credit' &&
      g.finished_at !== null &&
      new Date(g.finished_at).getTime() >= mountedAt,
  );
  const status = statusQ.data;
  const blockReason = !status
    ? null
    : !status.configured
      ? 'Генерация видео не настроена: добавьте ключ OpenRouter в настройках'
      : !canEdit
        ? 'Роль «наблюдатель»: генерировать может редактор'
        : status.balance_ok === false || hit402
          ? NO_CREDIT_MESSAGE
          : busy
            ? 'Дождитесь окончания текущей генерации'
            : null;
  const canStart = Boolean(status) && blockReason === null && !start.isPending;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setSelected('new');
    const result = await checkImage(file);
    if (typeof result === 'string') {
      setPicked(null);
      setPickError(result);
    } else {
      setPicked(result);
      setPickError(null);
    }
  };

  const submit = () => {
    if (!picked) return;
    setUploadPct(0);
    start.mutate(
      { tab, file: picked.file },
      {
        onSuccess: (created) => {
          setPicked(null);
          setSelected(created.id);
        },
        onSettled: () => setUploadPct(null),
      },
    );
  };

  const onRetry = (generationId: Id) =>
    retry.mutate(generationId, {
      onSuccess: (created) => setSelected(created.id),
    });

  const retentionDays = status?.retention_days;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>Генерация видео</h1>
          <p className={styles.hint}>
            Seedance 1.5 Pro: 6 секунд, 720p, без звука, примерно $0.16 за ролик
          </p>
        </div>
        <div className={styles.headerTools}>
          <Seg
            value={tab}
            onChange={(next) => {
              setTab(next);
              setSelected('new');
            }}
            options={TABS}
            ariaLabel="Сценарий генерации"
          />
        </div>
      </header>

      <div className={styles.layout}>
        <aside className={styles.list}>
          <div className={styles.listHead}>
            <span className={styles.blockTitle}>генерации</span>
            <span className={styles.blockNote}>{listQ.data?.total ?? 0}</span>
            <Button size="xs" className={styles.newBtn} onClick={() => setSelected('new')}>
              <Plus size={12} />
              Новая
            </Button>
          </div>
          {isAdmin && listQ.data?.total_cost_usd != null && (
            <div className={styles.listTotal}>
              Потрачено всего: <b>{money(listQ.data.total_cost_usd)}</b>
            </div>
          )}

          {listQ.error && <ErrorBanner error={listQ.error} onRetry={() => void listQ.refetch()} />}

          {generations.length === 0 ? (
            <div className={styles.empty}>
              <b>Генераций пока нет</b>
              <span>
                Загрузите картинку справа — ролик появится здесь
                {retentionDays === undefined ? '' : ` и останется ${daysLabel(retentionDays)}`}.
              </span>
            </div>
          ) : (
            <div className={styles.items}>
              {generations.map((g) => (
                <GenerationCard
                  key={g.id}
                  generation={g}
                  picked={selected === g.id}
                  showAuthor={isAdmin}
                  onPick={() => setSelected(g.id)}
                />
              ))}
            </div>
          )}
        </aside>

        <section className={styles.pane}>
          {selected === 'new' && (
            <>
              <div
                className={`${styles.drop} ${dragActive ? styles.dropOn : ''} ${canEdit ? '' : styles.dropOff}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (canEdit) setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  if (canEdit) void onFile(e.dataTransfer.files[0]);
                }}
                onClick={() => canEdit && inputRef.current?.click()}
                role="button"
                tabIndex={0}
                aria-disabled={!canEdit}
              >
                <ImageUp size={26} strokeWidth={1.5} />
                <b>
                  {canEdit
                    ? `${TABS.find((t) => t.value === tab)?.label}: перетащите картинку или выберите файл`
                    : 'Роль «наблюдатель»: генерировать может редактор'}
                </b>
                <span>
                  JPG или PNG до {VIDEO_GEN_MAX_MB} МБ, стороны от {VIDEO_GEN_MIN_SIDE_PX} px, одна
                  не длиннее другой в 2,5 раза
                </span>
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  hidden
                  onChange={(e) => {
                    void onFile(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </div>

              <section className={styles.block}>
                <div className={styles.blockHead}>
                  <span className={styles.blockTitle}>
                    {TABS.find((t) => t.value === tab)?.label}
                  </span>
                  <span className={styles.blockNote}>{TAB_HINTS[tab]}</span>
                </div>

                {picked && (
                  <div className={styles.blockBody}>
                    <img className={styles.preview} src={picked.url} alt="Стартовый кадр" />
                    <div className={styles.facts}>
                      <b>{picked.file.name}</b>
                      <span className={styles.blockNote}>
                        {picked.width}×{picked.height} ·{' '}
                        {(picked.file.size / (1024 * 1024)).toFixed(1)} МБ
                      </span>
                    </div>
                  </div>
                )}

                {start.error && <ErrorBanner error={start.error} />}

                <div className={styles.blockFoot}>
                  {(pickError || blockReason) && (
                    <span
                      className={`${styles.reason} ${pickError || blockReason === NO_CREDIT_MESSAGE ? styles.reasonWarn : ''}`}
                      role={pickError ? 'alert' : undefined}
                    >
                      {pickError ?? blockReason}
                    </span>
                  )}
                  {uploadPct !== null && (
                    <span className={styles.blockNote}>Загружаем… {uploadPct}%</span>
                  )}
                  <Button variant="primary" onClick={submit} disabled={!canStart || !picked}>
                    {start.isPending ? 'Отправляем…' : 'Сгенерировать'}
                  </Button>
                </div>
              </section>
            </>
          )}

          {generation && (
            <GenerationView
              generation={generation}
              retentionDays={retentionDays}
              canRetry={canStart}
              retrying={retry.isPending}
              retryError={retry.error}
              onRetry={() => onRetry(generation.id)}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function GenerationCard({
  generation,
  picked,
  showAuthor,
  onPick,
}: {
  generation: VideoGeneration;
  picked: boolean;
  showAuthor: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.card} ${picked ? styles.cardOn : ''}`}
      onClick={onPick}
    >
      <span className={styles.cardTop}>
        <b>{generation.tab_label}</b>
        <Pill tone={STATUS_TONES[generation.status]}>{STATUS_LABELS[generation.status]}</Pill>
        <span className={styles.cardAgo}>{ago(generation.created_at)}</span>
      </span>
      <span className={styles.cardFoot}>
        <span>{generation.aspect_ratio}</span>
        {generation.duration_s !== null && <span>· {Math.round(generation.duration_s)} с</span>}
        <span className={styles.cardAgo}>{money(generation.cost_usd)}</span>
      </span>
      {showAuthor && generation.author && (
        <span className={styles.cardOwner}>{authorLabel(generation.author)}</span>
      )}
    </button>
  );
}

function stepState(generation: VideoGeneration, index: number): string {
  const current = STEPS.findIndex((s) => s.key === (generation.stage ?? 'submitted'));
  if (generation.status === 'done') return 'done';
  if (index < current) return 'done';
  if (index === current) return generation.status === 'failed' ? 'fail' : 'now';
  return 'next';
}

function GenerationView({
  generation,
  retentionDays,
  canRetry,
  retrying,
  retryError,
  onRetry,
}: {
  generation: VideoGeneration;
  retentionDays: number | undefined;
  canRetry: boolean;
  retrying: boolean;
  retryError: Parameters<typeof ErrorBanner>[0]['error'] | null;
  onRetry: () => void;
}) {
  return (
    <>
      <div className={styles.phases}>
        <div className={styles.phaseHead}>
          <b>{generation.tab_label}</b>
          <Pill tone={STATUS_TONES[generation.status]}>{STATUS_LABELS[generation.status]}</Pill>
          <span className={styles.blockNote}>
            {generation.aspect_ratio}
            {generation.duration_s !== null && ` · ${Math.round(generation.duration_s)} с`}
          </span>
          <span className={styles.newBtn}>
            <span className={styles.cost}>{money(generation.cost_usd)}</span>
          </span>
        </div>
        <div className={styles.phaseTrack}>
          {STEPS.map((step, i) => (
            <div
              key={step.key}
              className={`${styles.phaseStep} ${styles[`step_${stepState(generation, i)}`]}`}
            >
              <span className={styles.phaseBar} />
              <span>{step.label}</span>
            </div>
          ))}
        </div>
      </div>

      {generation.status === 'failed' && (
        <div className={styles.failBox} role="alert">
          <AlertCircle size={16} strokeWidth={1.5} />
          <div>
            <b>Ролик не получился</b>
            <span>{generation.error}</span>
          </div>
          {!generation.files_expired && (
            <span className={styles.failActions}>
              <Button size="sm" onClick={onRetry} disabled={!canRetry || retrying}>
                <RotateCcw size={12} />
                Повторить
              </Button>
            </span>
          )}
        </div>
      )}
      {retryError && <ErrorBanner error={retryError} />}

      {generation.files_expired && (
        <div className={styles.expiredBox}>
          Файлы удалены через{' '}
          {retentionDays === undefined ? 'срок хранения' : daysLabel(retentionDays)}. Запись и
          стоимость остались.
        </div>
      )}

      {generation.video && (
        <section className={styles.block}>
          <video className={styles.player} src={generation.video.url} controls playsInline />
          <div className={styles.blockFoot}>
            <Button
              size="sm"
              onClick={() =>
                void downloadFile(
                  generation.download?.url ?? generation.video?.url ?? '',
                  `video-${generation.id.slice(0, 8)}.mp4`,
                )
              }
            >
              <Download size={12} />
              Скачать mp4
            </Button>
          </div>
        </section>
      )}
    </>
  );
}
